import React, { ReactElement, useCallback, useState } from "react";

import classNames from "classnames";
import { IconThumbDown, IconThumbUp } from "../icons";
import Tooltip from "../../tooltip";
import Icon from "../../icon";
import { MessageActionsBarProps } from "./MessageActionsBar.types";

const getIconName = (isCopySuccess: boolean | null): string => {
  if (isCopySuccess === null) {
    return "documents-empty";
  }

  return isCopySuccess ? "check" : "document-cancel";
};

export const MessageActionsBar = ({
  className,
  sessionId,
  responseId,
  chatAnswerPlainText,
  removeFromCopiedText = "I hope that answers your question. Feel free to ask any follow-up questions!",
  rating,
  onRatingChangeHandler,
  ...rest
}: MessageActionsBarProps): ReactElement => {
  const [isCopySuccess, setIsCopySuccess] = useState<null | boolean>(null);

  const hasThumbsUp = rating === "THUMBS_UP";
  const hasThumbsDown = rating === "THUMBS_DOWN";

  const copyToClipboard = useCallback((): void => {
    navigator.clipboard
      .writeText(
        (chatAnswerPlainText || "").replaceAll(removeFromCopiedText, "").trim()
      )
      .then(
        () => {
          setIsCopySuccess(true);
        },
        () => {
          setIsCopySuccess(false);
        }
      )
      .finally(() => {
        setTimeout(() => {
          setIsCopySuccess(null);
        }, 10000);
      });
  }, [chatAnswerPlainText, removeFromCopiedText]);

  return (
    <div
      className={classNames("spark-mar-t-1 spark-mar-b-2", className)}
      {...rest}
    >
      <Tooltip
        anchorX="center"
        anchorY="top"
        z-index={1000}
        toggleEl={
          <button
            className="spark-btn--icon spark-icon"
            style={{ height: 32, width: 32, padding: 0, lineHeight: 0 }}
            onClick={copyToClipboard}
            aria-label="copy-button"
          >
            <Icon name={getIconName(isCopySuccess)} size="default" />
          </button>
        }
      >
        {isCopySuccess ? "Copied!" : "Copy to clipboard"}
      </Tooltip>

      <Tooltip
        anchorX="center"
        anchorY="top"
        z-index={1000}
        toggleEl={
          <button
            className="spark-btn--icon spark-icon"
            style={{
              height: 32,
              width: 32,
              padding: "0.5rem 0 0.5rem 0.5rem",
              lineHeight: 0,
            }}
            onClick={(): void =>
              onRatingChangeHandler(sessionId, responseId, "THUMBS_UP")
            }
            aria-label="upvote-button"
          >
            <IconThumbUp
              style={{ fill: hasThumbsUp ? "initial" : "transparent" }}
            />
          </button>
        }
      >
        {hasThumbsUp ? "Up voted!" : "Up vote"}
      </Tooltip>

      <Tooltip
        anchorX="center"
        anchorY="top"
        z-index={1000}
        toggleEl={
          <button
            className="spark-btn--icon spark-icon"
            style={{
              height: 32,
              width: 32,
              padding: "0.75rem 0 0.75rem 0.5rem",
              lineHeight: 0,
            }}
            onClick={(): void =>
              onRatingChangeHandler(sessionId, responseId, "THUMBS_DOWN")
            }
            aria-label="downvote-button"
          >
            <IconThumbDown
              style={{ fill: hasThumbsDown ? "initial" : "transparent" }}
            />
          </button>
        }
      >
        {hasThumbsDown ? "Down voted!" : "Down vote"}
      </Tooltip>
    </div>
  );
};
